import { z } from 'zod';
import {
  FieldKeySchema,
  IdSchema,
  IsoDateTimeSchema,
  LogicalDataTypeSchema,
  MetadataSchema,
  ScalarValueSchema,
  SchemaVersionSchema,
  TimeGrainSchema,
} from './common.js';
import { AnalysisStateSchema, MAX_QUERY_LIMIT } from './analysis-state.js';
import { FormatSpecSchema } from './format.js';

// -----------------------------------------------------------------------------
// Query Request
// -----------------------------------------------------------------------------

export const QUERY_ORIGINS = ['ui', 'ai', 'api', 'schedule', 'n8n'] as const;

/**
 * Solicitud de ejecucion analitica: intencion (AnalysisState) + opciones de ejecucion.
 * No contiene SQL: el Query Engine la convierte en un plan logico y cada dialecto
 * la compila para su motor.
 */
export const QueryRequestSchema = z
  .strictObject({
    schemaVersion: SchemaVersionSchema,
    id: IdSchema,
    semanticModelId: IdSchema,
    analysisState: AnalysisStateSchema,
    /** Subconjunto de campos del analisis a devolver; si se omite, todos. */
    fields: z
      .strictObject({
        dimensionKeys: z.array(FieldKeySchema).default([]),
        measureKeys: z.array(FieldKeySchema).default([]),
      })
      .optional(),
    execution: z
      .strictObject({
        timeoutMs: z.number().int().min(100).max(600_000).default(30_000),
        maxRows: z.number().int().positive().max(MAX_QUERY_LIMIT).default(100_000),
        /** Valida y planifica sin ejecutar. */
        dryRun: z.boolean().default(false),
        includeTotals: z.boolean().default(true),
        priority: z.enum(['interactive', 'background']).default('interactive'),
      })
      .prefault({}),
    pagination: z
      .strictObject({
        offset: z.number().int().nonnegative().default(0),
        pageSize: z.number().int().min(1).max(100_000).default(1000),
      })
      .prefault({}),
    cache: z
      .strictObject({
        mode: z.enum(['default', 'bypass', 'refresh', 'only']).default('default'),
        maxAgeSeconds: z.number().int().nonnegative().optional(),
      })
      .prefault({}),
    metadata: z
      .strictObject({
        requestedAt: IsoDateTimeSchema.optional(),
        origin: z.enum(QUERY_ORIGINS).default('ui'),
        correlationId: IdSchema.optional(),
        locale: z.string().min(2).max(35).optional(),
      })
      .prefault({}),
  })
  .superRefine((request, ctx) => {
    if (request.analysisState.semanticModelId !== request.semanticModelId) {
      ctx.addIssue({
        code: 'custom',
        path: ['analysisState', 'semanticModelId'],
        message: 'El analisis debe referenciar el mismo Semantic Model que la solicitud.',
      });
    }
    if (!request.fields) return;
    const dimensionKeys = new Set(request.analysisState.dimensions.map((d) => d.key));
    const measureKeys = new Set(request.analysisState.measures.map((m) => m.key));
    request.fields.dimensionKeys.forEach((key, i) => {
      if (!dimensionKeys.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', 'dimensionKeys', i],
          message: `La dimension "${key}" no existe en el analisis.`,
        });
      }
    });
    request.fields.measureKeys.forEach((key, i) => {
      if (!measureKeys.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', 'measureKeys', i],
          message: `La medida "${key}" no existe en el analisis.`,
        });
      }
    });
  });
export type QueryRequest = z.infer<typeof QueryRequestSchema>;
export type QueryRequestInput = z.input<typeof QueryRequestSchema>;

// -----------------------------------------------------------------------------
// Query Result
// -----------------------------------------------------------------------------

export const QueryResultColumnSchema = z.strictObject({
  key: FieldKeySchema,
  label: z.string().min(1).max(200),
  dataType: LogicalDataTypeSchema,
  role: z.enum(['dimension', 'measure', 'comparison']),
  /** `dimensionId` o `measureId` del Semantic Model que origina la columna. */
  fieldId: IdSchema,
  timeGrain: TimeGrainSchema.optional(),
  format: FormatSpecSchema.optional(),
});
export type QueryResultColumn = z.infer<typeof QueryResultColumnSchema>;

/** Fila posicional alineada con `columns`. Fechas en ISO 8601; decimales grandes como texto. */
export const QueryResultRowSchema = z.array(ScalarValueSchema);
export type QueryResultRow = z.infer<typeof QueryResultRowSchema>;

export const QueryWarningSchema = z.strictObject({
  code: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
  message: z.string().min(1).max(2000),
  path: z.string().max(200).optional(),
});
export type QueryWarning = z.infer<typeof QueryWarningSchema>;

/**
 * Resultado normalizado de una consulta analitica. Identico sin importar el motor que
 * lo produjo (DuckDB, PostgreSQL, Databricks, archivos...): `engine` es solo informativo.
 */
export const QueryResultSchema = z
  .strictObject({
    schemaVersion: SchemaVersionSchema,
    requestId: IdSchema,
    semanticModelId: IdSchema,
    analysisStateId: IdSchema,
    columns: z.array(QueryResultColumnSchema),
    rows: z.array(QueryResultRowSchema),
    totals: z
      .strictObject({
        grandTotal: QueryResultRowSchema.optional(),
        subtotals: z
          .array(
            z.strictObject({
              /** Valores de las dimensiones que definen el subtotal. */
              group: z.record(FieldKeySchema, ScalarValueSchema),
              values: QueryResultRowSchema,
            }),
          )
          .default([]),
      })
      .optional(),
    pagination: z.strictObject({
      offset: z.number().int().nonnegative(),
      pageSize: z.number().int().positive(),
      returnedRows: z.number().int().nonnegative(),
      totalRows: z.number().int().nonnegative().nullable(),
      hasMore: z.boolean(),
    }),
    execution: z.strictObject({
      executedAt: IsoDateTimeSchema,
      durationMs: z.number().nonnegative(),
      /** Identificador opaco del motor; el frontend no debe depender de el. */
      engine: IdSchema,
      cache: z.strictObject({
        hit: z.boolean(),
        ageSeconds: z.number().int().nonnegative().optional(),
      }),
      truncated: z.boolean().default(false),
    }),
    warnings: z.array(QueryWarningSchema).default([]),
    metadata: MetadataSchema.optional(),
  })
  .superRefine((result, ctx) => {
    const width = result.columns.length;
    const checkRow = (row: readonly unknown[], path: (string | number)[]): void => {
      if (row.length !== width) {
        ctx.addIssue({
          code: 'custom',
          path,
          message: `La fila tiene ${row.length} valores y hay ${width} columnas.`,
        });
      }
    };
    result.rows.forEach((row, i) => {
      checkRow(row, ['rows', i]);
    });
    if (result.totals?.grandTotal) checkRow(result.totals.grandTotal, ['totals', 'grandTotal']);
    result.totals?.subtotals.forEach((subtotal, i) => {
      checkRow(subtotal.values, ['totals', 'subtotals', i, 'values']);
    });
    if (result.pagination.returnedRows !== result.rows.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['pagination', 'returnedRows'],
        message: 'returnedRows debe coincidir con el numero de filas.',
      });
    }
  });
export type QueryResult = z.infer<typeof QueryResultSchema>;
export type QueryResultInput = z.input<typeof QueryResultSchema>;
